/**
 * Document TEXT builders (DOM-free product content).
 *
 * Faithful ports of the monolith's build*Text / printActiveCaseTabText /
 * buildJurorPsychProfile / buildChallengeAnalysis functions. Each RETURNS a
 * string (plain text, or the doc-body HTML for the psych/challenge builders).
 * The monolith global `state`/`saveState()` are replaced with
 * `store.getState()`; scoring reads use `scoringContext()`. These builders do
 * NOT open print windows, touch the doc viewer, or persist anything — those are
 * side effects handled by a separate module.
 *
 * ALL legal boilerplate, MA court formatting, headings, and citation prose
 * (Batson/Soares/Mathews/Dickerson/etc.) are preserved VERBATIM.
 */

import { store, scoringContext } from '@/app/store';
import { scoreJuror, getScoreLabel, getFlags, jurorAge } from '@/core/scoring';
import { generateJurorVdq } from '@/core/analysis';
import { getDetectedCharges, allChargeText } from '@/core/citations';
import { CHARGES } from '@/data/charges';
import { CASELAW } from '@/data/caselaw';
import { getCaselawDb } from '@/data/caselaw-db-instance';
import { byId, inputValue } from '@/ui/dom';
import type { CaselawEntry, Juror, Side } from '@/types';

// ─── LOCAL HELPERS (not part of any shared module) ───────────────────────────

/** m/d/yyyy — matches the monolith's todayStr(). */
function todayStr(): string {
  const d = new Date();
  return d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear();
}

/** True when a checkbox input with the given id is present and checked. */
function isChecked(id: string): boolean {
  const el = byId<HTMLInputElement>(id);
  return !!(el && el.checked);
}

/** Plain-text case summary block shared by several packet builders. */
function getCaseSummaryText(): string {
  const s = store.getState();
  const charges =
    getDetectedCharges(allChargeText(s.chargeList, s.chargeDescription))
      .map((k) => (CHARGES[k] ? CHARGES[k].label : k))
      .join(', ') || '(not specified)';
  const atty = s.opp || '(not specified)';
  const defAtty = s.defenseAtty || '(not specified)';
  const elements =
    (s.crimeElements || []).map((e, i) => '  ' + (i + 1) + '. ' + e).join('\n') ||
    (s.chargeDescription
      ? '  (See charge description: ' + s.chargeDescription + ')'
      : '  (none entered)');
  const facts = s.indictmentFacts || '(none entered)';
  return (
    'Case: ' +
    s.caseName +
    '\nDocket #: ' +
    (s.docket || 'N/A') +
    '\nCourt: ' +
    s.court +
    '\nNext Event Date: ' +
    s.trialDate +
    '\nJudge: ' +
    s.judge +
    '\nProsecution/ADA: ' +
    atty +
    (s.prossBBO ? ' (BBO# ' + s.prossBBO + ')' : '') +
    '\nDefense Attorney: ' +
    defAtty +
    (s.defBBO ? ' (BBO# ' + s.defBBO + ')' : '') +
    '\nCharges: ' +
    charges +
    '\nStatute/Charge Description: ' +
    (s.chargeDescription || 'N/A') +
    '\n\nElements of the Charges:\n' +
    elements +
    '\n\nFacts from Indictment / Police Report:\n' +
    facts
  );
}

/** One-line-per-juror panel roster used by the voir dire packet. */
function getJurorPanelText(): string {
  const jurors = store.getState().jurors || [];
  if (!jurors.length) return '(No jurors loaded)';
  return jurors
    .map(
      (j) =>
        'Juror ' +
        j.panelId +
        ' | Age: ' +
        (j.age || '?') +
        ' | Gender: ' +
        (j.gender || '?') +
        ' | Education: ' +
        (j.edu || '?') +
        ' | Occupation: ' +
        (j.occ || '?') +
        ' | LE: ' +
        (j.le || 'None') +
        ' | Victim: ' +
        (j.victim || 'No')
    )
    .join('\n');
}

/** Top-5 individualized voir dire questions per juror, plain text. */
function getIndividualVDText(): string {
  const s = store.getState();
  const jurors = s.jurors || [];
  if (!jurors.length) return '(No jurors loaded)';
  let out = '';
  jurors.forEach((j) => {
    const qs = generateJurorVdq(j, s.side, s.activeCharges);
    out += '\n--- Juror ' + j.panelId + ' (' + j.occ + ', ' + j.age + ') ---\n';
    qs.slice(0, 5).forEach((q, i) => {
      out += i + 1 + '. [' + q.cat + '] ' + q.q + '\n\n';
    });
  });
  return out || '(No individual questions generated)';
}

/** Jurors sorted by the numeric portion of their panelId. */
function sortedJurorsByPanelId(): Juror[] {
  const jurors = (store.getState().jurors || []).slice();
  jurors.sort((a, b) => {
    let an = parseInt(String(a.panelId).replace(/[^0-9]/g, ''), 10);
    let bn = parseInt(String(b.panelId).replace(/[^0-9]/g, ''), 10);
    if (isNaN(an)) an = 0;
    if (isNaN(bn)) bn = 0;
    return an - bn;
  });
  return jurors;
}

// ─── MOTION IN LIMINE ────────────────────────────────────────────────────────

export function buildMotionText(): string {
  const state = store.getState();
  const party = state.side || 'prosecution';
  const partyLabel = party === 'prosecution' ? 'THE COMMONWEALTH' : 'THE DEFENDANT';
  const attorney =
    party === 'prosecution'
      ? state.opp || '[ADA NAME]'
      : state.defenseAtty || '[DEFENSE ATTORNEY NAME]';
  const attyTitle = party === 'prosecution' ? 'Assistant District Attorney' : 'Attorney for Defendant';
  const attyBBO = party === 'prosecution' ? state.prossBBO || '[BBO#]' : state.defBBO || '[BBO#]';
  const attyAddr = party === 'prosecution' ? state.prosAddr || '' : state.defAddr || '';
  const attyEmail = party === 'prosecution' ? state.prosEmail || '' : state.defEmail || '';
  const attyPhone = party === 'prosecution' ? state.prosPhone || '' : state.defPhone || '';
  const caseName = state.caseName || '[CASE NAME]';
  const court = state.court || '[COURT]';
  const county = state.county || '[COUNTY]';
  const judge = state.judge || '[JUDGE]';
  const docket = state.docket || '[DOCKET NO.]';
  const trialDate = state.trialDate || '[NEXT EVENT DATE]';
  const date = todayStr();
  const defendant = caseName.indexOf(' v. ') > -1 ? caseName.split(' v. ')[1].trim() : caseName;
  const L1 = '─'.repeat(60);
  return [
    'COMMONWEALTH OF MASSACHUSETTS',
    court.toUpperCase(),
    county.toUpperCase() + ' COUNTY, SS.',
    '',
    'COMMONWEALTH OF MASSACHUSETTS,',
    '                    Plaintiff,',
    '                                       Docket No. ' + docket,
    '        v.',
    '',
    defendant + ',',
    '                    Defendant.',
    L1,
    '',
    'MOTION IN LIMINE OF ' + partyLabel,
    'TO PERMIT LIMITED DIGITAL CAPTURE OF JUROR QUESTIONNAIRE DATA',
    'FOR USE WITH THE SPATCHAI JURY SELECTION APPLICATION',
    '',
    L1,
    '',
    // "Next Event Date", not "Trial Date": this is the next date counsel must
    // appear — usually a status conference or motion hearing, only occasionally
    // the trial. Calling it a trial date in a filed document would assert
    // something the docket does not say.
    'Next Event Date: ' + trialDate + '     Presiding Justice: Hon. ' + judge,
    '',
    L1,
    '',
    'NOW COMES ' + partyLabel + ' in the above-captioned matter and respectfully moves',
    'this Honorable Court, pursuant to Mass. R. Crim. P. 14(a)(2) and the',
    "Court's inherent authority to manage trial proceedings, for an Order",
    'permitting limited digital processing of juror questionnaire data',
    'for the sole purpose of facilitating voir dire through the SpatchAI',
    'jury selection application.',
    '',
    'I.  INTRODUCTION',
    '',
    'A.  SpatchAI Overview',
    '',
    '    SpatchAI is a locally-operated Progressive Web Application (PWA)',
    '    designed to assist trial counsel in voir dire preparation and juror',
    '    scoring. The application operates entirely on-device; no juror data',
    '    is transmitted to any external server, cloud service, or third-party',
    '    system at any time.',
    '',
    'II.  ARGUMENT',
    '',
    'A.  All Data Is Processed Entirely On-Device',
    '',
    "    SpatchAI processes all juror information exclusively on counsel's",
    '    device. No data is transmitted to any external server, cloud service,',
    '    or third-party system at any point.',
    '',
    'B.  Personally Identifying Information Is Excluded By Design',
    '',
    '    The application is programmatically configured to capture and retain',
    '    only the following non-identifying data fields:',
    '',
    '    • Gender (not name)',
    '    • Age (not date of birth)',
    '    • Education level',
    '    • Employment status and business type (not employer name)',
    '    • Occupation/job title',
    '    • Household/child-guardian status',
    '    • Law-related employment experience',
    '    • Impartiality and case-knowledge responses',
    '',
    '    The following fields are explicitly prohibited from capture:',
    '',
    '    • Full name, first name, or last name',
    '    • Date of birth or place of birth',
    '    • Home or mailing address',
    '    • Employer name',
    '    • Spouse or partner name',
    '',
    'C.  Data Is Deleted Upon Conclusion of Jury Selection',
    '',
    '    All juror data will be permanently deleted at the conclusion of',
    '    jury selection. Counsel will file a signed Affidavit of Counsel',
    '    Regarding Juror Data Destruction with the Court within five (5)',
    '    business days of the conclusion of jury selection.',
    '',
    'D.  No Juror Data Is Stored in the Cloud',
    '',
    '    SpatchAI does not use cloud storage, a server-side database, or',
    '    any remote API call that transmits juror information. All computation',
    "    occurs in the browser's local JavaScript runtime on counsel's device.",
    '',
    'E.  The Tool Serves the Interests of Justice',
    '',
    '    Use of SpatchAI advances the efficient administration of justice by',
    '    enabling counsel to systematically identify jurors who may harbor',
    '    implicit biases, law-enforcement connections, or prior experiences',
    '    affecting impartiality — all proper subjects of voir dire under',
    '    Massachusetts law. See Commonwealth v. Seguin, 421 Mass. 243 (1995);',
    '    G.L. c. 234A, §§ 67–68.',
    '',
    'III.  PROPOSED ORDER',
    '',
    '    WHEREFORE, ' + partyLabel + ' respectfully requests that this Court enter',
    '    an Order:',
    '',
    '    1.  Permitting counsel to use the SpatchAI application to process',
    '        non-identifying juror questionnaire data during jury selection;',
    '',
    '    2.  Confirming that such use is consistent with juror privacy',
    '        protections under G.L. c. 234A;',
    '',
    '    3.  Requiring counsel to file an Affidavit of Counsel Regarding',
    '        Juror Data Destruction within five (5) business days of the',
    '        conclusion of jury selection; and',
    '',
    '    4.  Granting such other and further relief as this Court deems just',
    '        and proper.',
    '',
    L1,
    '',
    '                         Respectfully submitted,',
    '                         ' + partyLabel + ',',
    '                         By ' + attyTitle + ',',
    '',
    '',
    '/s/ ' + attorney,
    '___________________________________',
    attorney,
    attyTitle,
    'BBO No. ' + attyBBO,
    attyAddr ? attyAddr.replace(/\n/g, ' ') : '',
    attyEmail ? attyEmail : '',
    attyPhone ? attyPhone : '',
    'Date: ' + date,
    '',
    L1,
    '',
    'AFFIDAVIT IN SUPPORT OF MOTION IN LIMINE',
    '',
    'I, ' + attorney + ', BBO No. ' + attyBBO + ', ' + attyTitle + ',',
    'being duly sworn under the pains and penalties of perjury,',
    'do hereby depose and state:',
    '',
    '    1.  I am counsel of record for ' + partyLabel + ' in the above-captioned',
    '        matter, ' + caseName + ', Docket No. ' + docket + ', scheduled for',
    '        trial on ' + trialDate + ' before the Honorable ' + judge + '.',
    '',
    '    2.  I intend to use the SpatchAI application during jury selection',
    '        solely to record non-identifying juror characteristics for voir',
    '        dire preparation and juror scoring purposes.',
    '',
    '    3.  SpatchAI operates entirely on my local device. No juror data is',
    '        transmitted to any external server or cloud service at any time.',
    '',
    '    4.  The application is programmatically restricted to capturing only',
    '        the non-identifying fields enumerated in Section II.B above.',
    '',
    '    5.  Upon completion of jury selection, I will permanently delete all',
    '        juror data from the application and will file a signed Affidavit',
    '        of Counsel Regarding Juror Data Destruction within five (5)',
    '        business days.',
    '',
    "    6.  I understand that my use of SpatchAI is subject to this Court's",
    '        Order and all applicable rules governing juror privacy.',
    '',
    'Signed under the pains and penalties of perjury this ' + date + '.',
    '',
    '',
    '/s/ ' + attorney,
    '___________________________________     _______________',
    attorney + '                                 Date',
    attyTitle,
    'BBO No. ' + attyBBO
  ]
    .filter((l) => l !== null && l !== undefined)
    .join('\n');
}

// ─── DYNAMIC AUTHORITY ASSEMBLY (rules + case law + statute) ─────────────────

/** Topics that make an authority relevant to the questionnaire-capture motion. */
const MOTION_TOPICS = [
  'juror privacy',
  'questionnaire',
  'recording',
  'record',
  'photograph',
  'photography',
  'image',
  'capture',
  'video',
  'voir dire',
  'access',
  'public trial',
  'closure',
  'juror data',
  'disclosure',
  'notes',
  'confidential',
  'courtroom',
  'media'
];

/** True if a curated authority's topics intersect the motion's topic set. */
function isMotionRelevant(e: CaselawEntry): boolean {
  return (e.topics || []).some((t) => {
    const lt = t.toLowerCase();
    return MOTION_TOPICS.some((m) => lt.includes(m) || m.includes(lt));
  });
}

/** Wrap a long string to indented lines (plain-text memo formatting). */
function wrapIndent(text: string, indent: string, width = 68): string {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    if ((line + ' ' + w).trim().length > width) {
      lines.push(indent + line.trim());
      line = w;
    } else {
      line = (line + ' ' + w).trim();
    }
  }
  if (line) lines.push(indent + line.trim());
  return lines.join('\n');
}

/**
 * Assemble the Table of Authorities + Statement of Applicable Authority by
 * pulling the on-point statutes, court rules, and cases from all three offline
 * collections (curated CASELAW + the offline rules database). Returns the two
 * formatted memo sections (III and IV) as plain text.
 */
export async function buildAuthoritiesBlock(): Promise<string> {
  const relevant = CASELAW.filter(isMotionRelevant);
  const statutes = relevant.filter((e) => e.type === 'statute');
  const curatedRules = relevant.filter((e) => e.type === 'rule');
  const cases = relevant.filter((e) => e.type === 'caselaw');

  // On-point rules from the offline rules DB (jurors / notes / recording).
  const dbRules: { citation: string; holding: string }[] = [];
  try {
    const db = await getCaselawDb();
    if (db) {
      const seen = new Set<string>();
      for (const q of ['jurors', 'notes by jurors', 'recording', 'photograph']) {
        for (const r of db.searchRules(q, 8)) {
          const title = (r.title || '').toLowerCase();
          const relevantRule =
            title.includes('juror') || title.includes('notes') || title.includes('recording');
          if (!relevantRule || !r.ruleNumber) continue;
          const key = `${r.ruleSet}|${r.ruleNumber}`;
          if (seen.has(key)) continue;
          seen.add(key);
          dbRules.push({
            citation: `${r.ruleNumber} (${r.ruleSet})${r.title ? ' — ' + r.title : ''}`,
            holding: ''
          });
        }
      }
    }
  } catch {
    /* DB optional; curated authorities still assemble. */
  }

  // ── III. TABLE OF AUTHORITIES ──
  const toa: string[] = ['III.  TABLE OF AUTHORITIES', ''];
  if (statutes.length) {
    toa.push('    Statutes');
    statutes.forEach((s) => toa.push('        ' + s.citation));
    toa.push('');
  }
  if (curatedRules.length || dbRules.length) {
    toa.push('    Court Rules');
    curatedRules.forEach((r) => toa.push('        ' + r.citation));
    dbRules.forEach((r) => toa.push('        ' + r.citation));
    toa.push('');
  }
  if (cases.length) {
    toa.push('    Cases');
    cases.forEach((c) => toa.push('        ' + c.citation));
    toa.push('');
  }

  // ── IV. STATEMENT OF APPLICABLE AUTHORITY ──
  const stmt: string[] = ['IV.  STATEMENT OF APPLICABLE AUTHORITY', ''];
  const emit = (label: string, entries: CaselawEntry[]): void => {
    if (!entries.length) return;
    stmt.push('    ' + label);
    stmt.push('');
    entries.forEach((e) => {
      stmt.push('        ' + e.citation);
      const holding = (e.holdings && e.holdings[0]) || e.summary || '';
      if (holding) stmt.push(wrapIndent(holding, '            '));
      stmt.push('');
    });
  };
  emit('A.  Statutes', statutes);
  emit('B.  Court Rules', curatedRules);
  if (dbRules.length) {
    stmt.push('        Additional court rules governing jurors and the conduct of');
    stmt.push('        proceedings (full text available in the SpatchAI offline');
    stmt.push('        rules library):');
    dbRules.forEach((r) => stmt.push('            ' + r.citation));
    stmt.push('');
  }
  emit('C.  Case Law', cases);
  stmt.push(
    '    The foregoing authorities are drawn from the application’s offline'
  );
  stmt.push(
    '    legal library. Counsel has verified, and the Court should confirm,'
  );
  stmt.push('    each citation against the official reporter.');
  stmt.push('');

  return toa.join('\n') + '\n' + stmt.join('\n');
}

/**
 * The motion in limine with the dynamically-assembled authorities (rules +
 * case law + statute) inserted before the proposed order. Falls back to the
 * static motion if assembly fails.
 */
export async function buildMotionTextFull(): Promise<string> {
  const base = buildMotionText();
  try {
    const block = await buildAuthoritiesBlock();
    return base.replace('III.  PROPOSED ORDER', block + '\nV.  PROPOSED ORDER');
  } catch {
    return base;
  }
}

// ─── ATTORNEY AFFIDAVIT — JUROR DATA DELETION & PRIVACY ──────────────────────

export function buildAttyAffidavitText(): string {
  const state = store.getState();
  const name = inputValue('aff-name').trim() || '[ATTORNEY NAME]';
  const bar = inputValue('aff-bar').trim() || '[BAR #]';
  const delDate = inputValue('aff-date').trim() || state.lastJurorDeletion?.timestamp || todayStr();
  const sig = inputValue('aff-sig').trim() || name;
  const caseName = state.caseName || '[CASE NAME]';
  const court = state.court || '[COURT]';
  const county = state.county || '[COUNTY]';
  const judge = state.judge || '[JUDGE]';
  const docket = state.docket || '[DOCKET NO.]';
  const trialDate = state.trialDate || '[NEXT EVENT DATE]';
  // The juror array is already empty by the time this affidavit gets produced
  // (per its own instructions: delete first, then attest) -- the count has to
  // come from the snapshot taken at deletion, not from live state.
  const jurorCount = state.lastJurorDeletion?.count ?? (state.jurors ? state.jurors.length : 0);
  const isPros = state.side === 'prosecution';
  const attyTitle = isPros ? 'Assistant District Attorney' : 'Attorney for Defendant';
  const attyAddr = isPros ? state.prosAddr || '' : state.defAddr || '';
  const attyEmail = isPros ? state.prosEmail || '' : state.defEmail || '';
  const attyPhone = isPros ? state.prosPhone || '' : state.defPhone || '';
  const defendant = caseName.indexOf(' v. ') > -1 ? caseName.split(' v. ')[1].trim() : caseName;
  const L1 = '─'.repeat(60);
  return [
    'COMMONWEALTH OF MASSACHUSETTS',
    court.toUpperCase(),
    county.toUpperCase() + ' COUNTY, SS.',
    '',
    'COMMONWEALTH OF MASSACHUSETTS,',
    '                    Plaintiff,',
    '                                       Docket No. ' + docket,
    '        v.',
    '',
    defendant + ',',
    '                    Defendant.',
    L1,
    '',
    'ATTORNEY AFFIDAVIT OF JUROR DATA DELETION',
    'AND PRIVACY COMPLIANCE',
    '',
    L1,
    '',
    'I, ' + name + ', BBO No. ' + bar + ', ' + attyTitle + ',',
    'being duly sworn under the pains and penalties of perjury pursuant',
    'to G.L. c. 268, § 1A, do hereby attest and state as follows:',
    '',
    '    1.  I served as counsel of record in the above-captioned matter,',
    '        ' + caseName + ', Docket No. ' + docket + ', ' + court + ',',
    '        which proceeded to jury selection on or about ' + trialDate,
    '        before the Honorable ' + judge + '.',
    '',
    '    2.  During jury selection, I utilized the SpatchAI jury selection',
    '        application to record non-identifying characteristics of',
    '        prospective jurors for the limited purpose of voir dire',
    '        preparation and juror scoring.',
    '',
    '    3.  A total of ' + jurorCount + ' juror record(s) were entered into the',
    '        SpatchAI application during the course of jury selection.',
    '',
    '    4.  On ' + delDate + ', I permanently deleted all juror data from the',
    '        SpatchAI application on every device on which it was used, by:',
    '',
    "        a.  Using the application's built-in data wipe function, which",
    '            cleared all stored juror and case records from local state;',
    '',
    '        b.  Clearing all browser cache, localStorage, service worker',
    '            cache, and client-side storage on each device; and',
    '',
    '        c.  Verifying that no juror records remained on any device.',
    '',
    '    5.  I further attest that:',
    '',
    '        a.  No juror data was at any time uploaded, transmitted, synced,',
    '            or backed up to any cloud server, remote database, or any',
    '            other external device or service;',
    '',
    '        b.  No personally identifying information (PII) regarding any',
    '            juror was ever stored — the application is programmatically',
    '            restricted to non-identifying fields only;',
    '',
    '        c.  The only information captured consisted of the following',
    '            non-identifying fields: gender, age, education level,',
    '            employment status, business/industry type, occupation type,',
    '            household/parental status, prior jury service, law-enforcement',
    '            connection, law-related employment, crime victim/accused',
    '            history category, stated impartiality, and case-knowledge;',
    '',
    '        d.  No full names, dates of birth, home addresses, employer',
    '            names, or other PII were entered into or retained by the',
    '            application at any time;',
    '',
    '        e.  No photographs, screenshots, exports, or copies of juror',
    '            questionnaires were retained in any form on any device; and',
    '',
    '        f.  SpatchAI was used solely for voir dire preparation and',
    '            juror scoring in this matter and for no other purpose.',
    '',
    '    6.  I understand that this affidavit may be filed with the Court',
    '        upon request and that a false statement herein subjects me to',
    '        criminal penalties and professional discipline.',
    '',
    L1,
    '',
    'Signed under the pains and penalties of perjury this ' + delDate + '.',
    '',
    '',
    '/s/ ' + sig,
    '___________________________________     _______________',
    name + '                                 Date',
    attyTitle,
    'BBO No. ' + bar,
    attyAddr ? attyAddr.replace(/\n/g, ', ') : '',
    attyEmail ? attyEmail : '',
    attyPhone ? attyPhone : ''
  ]
    .filter((l) => l !== null && l !== undefined)
    .join('\n');
}

// ─── PRINT JURORS BY JURY NUMBER ─────────────────────────────────────────────

export function buildJurorListText(): string {
  const state = store.getState();
  const ctx = scoringContext();
  const jurors = sortedJurorsByPanelId();
  if (!jurors.length) return '(No jurors on file)';
  const inclVD = isChecked('pj-vd-questions');
  const inclScores = isChecked('pj-scores');
  const inclFlags = isChecked('pj-flags');
  let out =
    'ALL JURORS BY JURY NUMBER\n' +
    '='.repeat(50) +
    '\n' +
    getCaseSummaryText() +
    '\n\n' +
    '='.repeat(50) +
    '\n\n';
  jurors.forEach((j) => {
    out += 'JUROR ' + j.panelId + ' (Seat ' + j.seat + ')\n' + '-'.repeat(40) + '\n';
    out +=
      'Gender: ' +
      (j.gender || '—') +
      ' | Age: ' +
      (j.age || '—') +
      ' | Education: ' +
      (j.edu || '—') +
      '\n';
    out +=
      'Employment: ' +
      (j.empStatus || '—') +
      ' | Business/Industry: ' +
      (j.bizType || '—') +
      ' | Occupation: ' +
      (j.occ || '—') +
      '\n';
    out +=
      'Household/Parental Status: ' +
      (j.parent || '—') +
      ' | Prior Jury Service: ' +
      (j.jury || '—') +
      '\n';
    out +=
      'Law Enforcement Connection: ' +
      (j.le || 'None') +
      ' | Law-Related Employment: ' +
      (j.lawEmp || 'None') +
      '\n';
    out +=
      'Victim History: ' +
      (j.victim || 'No') +
      ' | Accused/Family Accused: ' +
      (j.accused || 'No') +
      '\n';
    out +=
      'Stated Impartiality: ' +
      (j.fair || '—') +
      ' | Case Knowledge: ' +
      (j.caseKnow || '—') +
      ' | Opinion: ' +
      (j.opinion || '—') +
      '\n';
    if (j.notes) out += 'Notes: ' + j.notes + '\n';
    if (inclScores) {
      const sc = scoreJuror(j, ctx);
      const sl = getScoreLabel(sc, state.side);
      out += 'Score (' + state.side + ' perspective): ' + sl.label + '\n';
    }
    if (inclFlags) {
      const flags = getFlags(j, ctx);
      out +=
        'Flags: ' +
        (flags.length ? flags.map((f) => f.label + ' (' + f.side + ')').join(', ') : 'None') +
        '\n';
    }
    if (inclVD) {
      const qs = generateJurorVdq(j, state.side, state.activeCharges).slice(0, 5);
      out += 'Top Voir Dire Questions:\n';
      qs.forEach((q, i) => {
        out += '  ' + (i + 1) + '. [' + q.cat + '] ' + q.q + '\n';
      });
    }
    out += '\n';
  });
  return out;
}

// ─── VOIR DIRE PRINT PACKET ──────────────────────────────────────────────────

export function buildVoirDirePacketText(): string {
  const state = store.getState();
  const inclGeneral = isChecked('vdp-general');
  const inclCase = isChecked('vdp-case');
  const inclJurors = isChecked('vdp-jurors');
  const inclIndividual = isChecked('vdp-individual');
  let out = 'VOIR DIRE PACKET\n' + '='.repeat(50) + '\n' + getCaseSummaryText() + '\n\n';
  if (inclCase) {
    out += '\nELEMENTS OF THE CHARGES\n' + '='.repeat(40) + '\n';
    const els = state.crimeElements || [];
    if (els.length) {
      els.forEach((e, i) => {
        out += i + 1 + '. ' + e + '\n';
      });
    } else {
      out += '(none entered)\n';
    }
    out += '\n';
  }
  if (inclGeneral) {
    out += 'GENERAL VOIR DIRE QUESTIONS\n' + '='.repeat(40) + '\n';
    const generalQs = [
      'Do you understand that the defendant is presumed innocent unless proven guilty beyond a reasonable doubt?',
      'Would any personal experience, bias, or strongly held belief prevent you from applying this standard?',
      'Can you evaluate police officer testimony by the same standard you would apply to any other witness?',
      'Do you have any strong feelings about the charges in this case that might affect your impartiality?',
      'Have you or a close family member ever been the victim of a crime similar to the charges in this case?',
      'Do you have any connection to law enforcement, prosecution, or the defense bar?',
      'Have you served on a jury before? If so, what type of case and can you set aside that experience?',
      'Do you believe that someone charged with a crime probably did something wrong, even if not the exact charge?',
      "Are you able to follow the judge's instruction on the law even if you personally disagree with it?",
      'Would you require the defendant to testify in order to vote not guilty?',
      'Do you have any scheduling conflict or personal hardship that would affect your ability to serve?',
      'Is there anything about the nature of this case that would make it difficult for you to remain impartial?',
      'Can you commit to deciding this case solely on the evidence presented in court and the law as instructed?',
      'Have you read, heard, or seen anything about this case in the media or online?'
    ];
    generalQs.forEach((q, i) => {
      out += i + 1 + '. ' + q + '\n\n';
    });
  }
  if (inclJurors) {
    out += '\nJUROR PANEL INFORMATION\n' + '='.repeat(40) + '\n' + getJurorPanelText() + '\n';
  }
  if (inclIndividual) {
    out += '\nINDIVIDUAL VOIR DIRE QUESTIONS\n' + '='.repeat(40) + getIndividualVDText();
  }
  return out;
}

// ─── ACTIVE CASE TAB TEXT ────────────────────────────────────────────────────

export function printActiveCaseTabText(): string {
  const chargesTab = byId('case-tab-charges');
  const chargesVisible = !!chargesTab && chargesTab.style.display !== 'none';
  if (!chargesVisible) return getCaseSummaryText();
  // Build full charges-tab text (mirrors printActiveCaseTab charges branch)
  const s = store.getState();
  const court = s.court || '';
  const docket = s.docket || '';
  const cname = s.caseName || '(Unnamed Case)';
  const judge = s.judge || '';
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const sep = '='.repeat(60);
  const hdr =
    'COMMONWEALTH OF MASSACHUSETTS\n' +
    (court ? court.toUpperCase() : 'SUPERIOR COURT') +
    '\n' +
    (docket ? 'Docket No. ' + docket + '\n' : '') +
    sep +
    '\n' +
    cname +
    '\n' +
    (judge ? 'Presiding: ' + judge + '\n' : '') +
    'Printed: ' +
    today +
    '\n' +
    sep +
    '\n\n';
  const els = s.crimeElements || [];
  const analysisEl = byId('analysis-content');
  const analysisText = analysisEl ? (analysisEl.innerText || analysisEl.textContent || '').trim() : '';
  const chargeLabels =
    getDetectedCharges(allChargeText(s.chargeList, s.chargeDescription))
      .map((k) => (CHARGES[k] ? CHARGES[k].label : k))
      .join(', ') || '(not specified)';
  const caseNotes = s.caseNotes || inputValue('inp-case-notes') || '';
  return (
    hdr +
    'CHARGES, FACTS & JURY ANALYSIS\n' +
    '-'.repeat(40) +
    '\n\n' +
    'CHARGES:\n' +
    chargeLabels +
    '\n\n' +
    'STATUTE / CHARGE DESCRIPTION:\n' +
    (s.chargeDescription || '(none entered)') +
    '\n\n' +
    'FACTS FROM INDICTMENT / POLICE REPORT:\n' +
    (s.indictmentFacts || '(none entered)') +
    '\n\n' +
    (els.length
      ? 'ELEMENTS OF THE CHARGES:\n' +
        els.map((e, i) => i + 1 + '. ' + e).join('\n') +
        '\n\n'
      : '') +
    (analysisText ? 'JURY ANALYSIS:\n' + '-'.repeat(40) + '\n' + analysisText + '\n\n' : '') +
    (caseNotes ? 'CASE NOTES / TRIAL STRATEGY:\n' + caseNotes + '\n' : '')
  );
}

// ─── JUROR PSYCHOLOGICAL PROFILE ─────────────────────────────────────────────

export function buildJurorPsychProfile(j: Juror): string {
  const state = store.getState();
  const sc = scoreJuror(j, scoringContext());
  const sl = getScoreLabel(sc, state.side);
  const sentences: string[] = [];
  const le = j.le || 'None';
  const isVictim = !!j.victim && j.victim.indexOf('Yes') !== -1;
  const highEdu = (j.edu || '').match(/master|phd|doctoral|jd|law/i);
  const ageMid = jurorAge(j);
  const blueCollar =
    (j.bizType || '').match(/trades|construct|labor/i) ||
    (j.occ || '').toLowerCase().match(/mechanic|plumb|electr|firefight|construct|contractor/);
  if (le !== 'None' && le !== '') {
    sentences.push(
      'Juror has significant law enforcement ties, which research indicates correlates with pro-prosecution leanings and higher deference to government witnesses (Lieberman &amp; Sales, 2007).'
    );
  }
  if (isVictim) {
    sentences.push(
      'As a reported crime victim, this juror may carry implicit sympathies toward the complaining witness, which voir dire research shows can reduce receptiveness to reasonable-doubt arguments.'
    );
  }
  if (highEdu && (le === 'None' || le === '')) {
    sentences.push(
      "Graduate-educated jurors without law enforcement connections tend to be more analytical, more skeptical of government overreach, and more demanding of the Commonwealth's evidentiary burden."
    );
  }
  if (blueCollar && !isVictim && ageMid < 45) {
    sentences.push(
      'Blue-collar jurors without victimization history tend to understand working-class defendant contexts and may be more open to defense narratives around credibility and identity.'
    );
  }
  if (!sentences.length) {
    sentences.push(
      'This juror presents no strongly predictive psychological indicators based on the information collected; scoring is derived primarily from demographic and occupational baseline factors.'
    );
  }
  const chargeLabel = state.chargeDescription
    ? state.chargeDescription.split(/\n/)[0].slice(0, 80)
    : 'the current charges';
  sentences.push(
    'Based on the current charge profile (' +
      chargeLabel +
      '), this juror scores ' +
      sl.label +
      ' for the ' +
      (state.side === 'defense' ? 'defense' : 'prosecution') +
      ' side.'
  );
  return sentences.join(' ');
}

// ─── CHALLENGE ANALYSIS ──────────────────────────────────────────────────────

export function buildChallengeAnalysis(j: Juror, side: Side): string {
  const forCause: string[] = [];
  if (j.fair === 'No – stated cannot be fair') {
    forCause.push(
      'Juror stated an inability to be fair, which is grounds for a for-cause challenge. <em>Commonwealth v. Mathews</em>, 406 Mass. 380 (1990) (juror who cannot set aside bias must be excused for cause).'
    );
  }
  if (j.le && j.le !== 'None' && side === 'defense') {
    forCause.push(
      "Juror's law enforcement connection may support a for-cause challenge by the defense where impartiality is in doubt. <em>Commonwealth v. Dickerson</em>, 372 Mass. 783 (1977) (close relationship to law enforcement warrants heightened scrutiny during voir dire)."
    );
  }
  if (j.victim && j.victim.indexOf('Yes') !== -1 && side === 'defense') {
    forCause.push(
      "Juror's history as a crime victim may support a defense for-cause challenge given potential sympathy toward the Commonwealth's witnesses. <em>Cornu v. Commonwealth</em>, 407 Mass. 218 (impartiality concerns from victim experience should be explored fully on the record)."
    );
  }
  if (j.lawEmp === 'Attorney / Lawyer') {
    forCause.push(
      "Juror's legal training raises potential impartiality concerns, including the risk that the juror substitutes personal legal knowledge for the court's instructions."
    );
  }
  const sc = scoreJuror(j, scoringContext());
  const sl = getScoreLabel(sc, side);
  const favorable = (side === 'defense' && sc >= 2) || (side === 'prosecution' && sc <= -2);
  const peremptory =
    'Based on the current score (' +
    sl.label +
    '), this juror is ' +
    (favorable
      ? 'favorable and should generally be retained'
      : 'unfavorable and is a reasonable candidate for a peremptory strike') +
    ' from the ' +
    side +
    ' perspective. Any peremptory challenge must be exercised for a race- and gender-neutral reason. <em>Batson v. Kentucky</em>, 476 U.S. 79 (1986); <em>Commonwealth v. Soares</em>, 377 Mass. 461 (1979) (prohibiting peremptory challenges based on group bias).';
  const opposing =
    "Opposing counsel may argue that this juror's demographic and background factors alone do not establish bias sufficient to justify a challenge, and that any peremptory strike targeting protected characteristics would violate <em>Soares</em>/<em>Batson</em>. Counsel should be prepared to articulate a specific, case-related, non-pretextual basis for any challenge exercised, tied to the juror's stated answers rather than group membership.";
  let html = '';
  html +=
    '<div style="margin-bottom:8px;"><div class="dl" style="margin-bottom:3px;">For-Cause Grounds</div>' +
    '<div style="font-size:11px;color:var(--text-secondary);line-height:1.7;">' +
    (forCause.length
      ? forCause.join('<br><br>')
      : 'No clear for-cause basis identified from the information collected; a peremptory challenge may still be considered.') +
    '</div></div>';
  html +=
    '<div style="margin-bottom:8px;"><div class="dl" style="margin-bottom:3px;">Peremptory Challenge Recommendation</div>' +
    '<div style="font-size:11px;color:var(--text-secondary);line-height:1.7;">' +
    peremptory +
    '</div></div>';
  html +=
    '<div><div class="dl" style="margin-bottom:3px;">Opposing Counsel Arguments</div>' +
    '<div style="font-size:11px;color:var(--text-secondary);line-height:1.7;">' +
    opposing +
    '</div></div>';
  return html;
}
